import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useData } from "@/lib/store";
import { TYPE_META } from "@/lib/utils";

export default function CaptureDialog({ open, onOpenChange }) {
  const { addItem, refresh } = useData();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || loading) return;
    setLoading(true);
    try {
      const r = await api.post("/capture", { text });
      addItem(r.data.item);
      setLastResult(r.data);
      setText("");
      toast.success(`Captured as ${TYPE_META[r.data.item.type].label}`, {
        description: `Filed to ${r.data.classified.space || "Life admin"}`,
      });
      await refresh();
    } catch (err) {
      toast.error("Capture failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-surface rounded-hero border-none p-0 overflow-hidden" data-testid="capture-dialog">
        <div className="px-6 pt-6 pb-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-surface-on text-lg font-medium">
              <Sparkles className="w-4 h-4 text-primary" /> Quick capture
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-surface-onVariant mt-1">Type anything — the Brain will classify it.</p>
        </div>
        <form onSubmit={submit} className="px-6 pb-6">
          <textarea
            data-testid="capture-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            rows={3}
            className="w-full bg-surface-container rounded-t-lg rounded-b-none border-b-2 border-outline focus:border-primary outline-none px-4 py-3 text-surface-on resize-none transition-colors duration-200 ease-md"
            placeholder="e.g. Renew driver's licence next month, pay BAS Friday, call the accountant…"
          />
          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-surface-onVariant">Enter to submit</div>
            <button
              data-testid="capture-submit-btn"
              type="submit"
              disabled={loading || !text.trim()}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-medium px-5 py-2.5 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md"
            >
              <Send className="w-4 h-4" />
              {loading ? "Classifying…" : "Capture"}
            </button>
          </div>
        </form>
        {lastResult && (
          <div className="px-6 pb-6 -mt-2">
            <div className="bg-primary-container/60 rounded-2xl px-4 py-3 text-sm text-primary-onContainer" data-testid="capture-result">
              <div className="font-medium">Filed as {TYPE_META[lastResult.item.type].label}</div>
              <div className="text-primary-onContainer/80 text-xs mt-1 truncate">{lastResult.item.title}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
